/**
 * Employee Numbering — System Status card (4.36.1, Part A).
 *
 * Read-mostly view of the numbering health an admin/HR would otherwise run a
 * script for: active employees with no number, duplicate groups (by SSN+DOB
 * hash and by name+DOB), the permanent ledger size, the reserved block, and
 * whether secure SSN+DOB matching is enabled. No SSNs/DOBs are ever shown.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Link } from 'react-router-dom';
import { Hash, AlertTriangle, CheckCircle2, RefreshCw, Users, ShieldCheck, ShieldOff } from 'lucide-react';

const Stat = ({ label, value, tone = 'default', testid }) => (
  <div className="rounded-lg border p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : tone === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
      {value}
    </p>
  </div>
);

export default function EmployeeNumberingCard() {
  const [health, setHealth] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, p, m] = await Promise.all([
        api.get('/employees/number-health'),
        api.get('/employees/number-policy'),
        api.get('/employees/match-admin/status'),
      ]);
      setHealth(h.data); setPolicy(p.data); setMatch(m.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read employee numbering status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post('/employees/match-admin/backfill');
      toast.success(`Hashed ${data.updated} record(s); ${data.skipped_no_data} had no SSN+DOB`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally { setBackfilling(false); }
  }, [load]);

  const matchOn = health?.ssn_dob_matching_enabled;
  const noNum = health?.active_without_number;
  const dupHash = health?.duplicate_by_ssn_dob_hash;
  const dupName = health?.duplicate_by_name_dob;

  return (
    <Card data-testid="employee-numbering-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Hash className="h-5 w-5" />Employee numbering</CardTitle>
            <CardDescription>
              Permanent used-numbers ledger, duplicate detection and the reserved TriCorps 360 block. No SSNs or dates of birth are shown.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="numbering-refresh-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!matchOn && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:bg-amber-950/30" data-testid="hmac-disabled-warning">
            <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm">
              <p className="font-semibold text-amber-800 dark:text-amber-300">Secure SSN+DOB matching is OFF</p>
              <p className="text-amber-700 dark:text-amber-400">{health?.warning || 'EMPLOYEE_MATCH_HMAC_KEY is not configured — imports still match by employee number.'}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat testid="stat-ledger-size" label="Numbers ever used" value={health?.ledger_size ?? '—'} />
          <Stat testid="stat-no-number" label="Active, no number" value={noNum?.count ?? '—'} tone={noNum?.count ? 'warn' : 'default'} />
          <Stat testid="stat-dup-hash" label="Dup by SSN+DOB" value={dupHash?.count ?? '—'} tone={dupHash?.count ? 'bad' : 'default'} />
          <Stat testid="stat-dup-name" label="Dup by name+DOB" value={dupName?.count ?? '—'} tone={dupName?.count ? 'warn' : 'default'} />
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge variant="outline" className="gap-1" data-testid="reserved-block-badge">
            Reserved block {policy?.reserved_start}–{policy?.reserved_end}
          </Badge>
          <Badge variant={matchOn ? 'default' : 'secondary'} className="gap-1">
            {matchOn ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
            SSN+DOB matching {matchOn ? 'enabled' : 'disabled'}
          </Badge>
          <span className="text-muted-foreground">{match?.employees_with_hash ?? 0} of {match?.employees_total ?? 0} records hashed</span>
          {match?.enabled && (
            <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfilling} data-testid="numbering-backfill-btn">
              {backfilling ? 'Hashing…' : 'Backfill hashes'}
            </Button>
          )}
        </div>

        {dupHash?.count > 0 && (
          <div data-testid="dup-hash-list">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-700"><AlertTriangle className="h-4 w-4" />Likely duplicates (same SSN + date of birth)</p>
            <div className="space-y-2">
              {dupHash.groups.map((g, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm">
                  {g.members.map((m) => (
                    <Link key={m.id} to={`/employees/${m.id}`} className="rounded bg-muted px-2 py-1 text-foreground hover:underline">
                      {m.first_name} {m.last_name} <span className="text-muted-foreground">#{m.employee_number || '—'}</span>
                    </Link>
                  ))}
                  <Link to="/admin/employee-merge" className="ml-auto text-xs font-medium text-blue-600 hover:underline">Merge…</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {dupName?.count > 0 && (
          <div data-testid="dup-name-list">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-700"><Users className="h-4 w-4" />Possible duplicates (same name + date of birth)</p>
            <div className="space-y-2">
              {dupName.groups.slice(0, 50).map((g, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm">
                  {g.members.map((m) => (
                    <Link key={m.id} to={`/employees/${m.id}`} className="rounded bg-muted px-2 py-1 text-foreground hover:underline">
                      {m.first_name} {m.last_name} <span className="text-muted-foreground">#{m.employee_number || '—'}</span>
                    </Link>
                  ))}
                  <Link to="/admin/employee-merge" className="ml-auto text-xs font-medium text-blue-600 hover:underline">Merge…</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {noNum?.count > 0 && (
          <div data-testid="no-number-list">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-700"><AlertTriangle className="h-4 w-4" />Active employees with no number ({noNum.count})</p>
            <div className="flex flex-wrap gap-2">
              {noNum.employees.slice(0, 60).map((e) => (
                <Link key={e.id} to={`/employees/${e.id}`} className="rounded bg-muted px-2 py-1 text-sm text-foreground hover:underline">{e.name}</Link>
              ))}
              {noNum.count > 60 && <span className="text-xs text-muted-foreground">+{noNum.count - 60} more</span>}
            </div>
          </div>
        )}

        {noNum?.count === 0 && dupHash?.count === 0 && dupName?.count === 0 && (
          <p className="flex items-center gap-2 text-sm text-emerald-600" data-testid="numbering-all-clear"><CheckCircle2 className="h-4 w-4" />No numbering issues detected.</p>
        )}
      </CardContent>
    </Card>
  );
}
